import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '..';
import type { StorageItem } from '../../services/Storage';

// Firebase
import { inboxFirestore } from '../../services/firebase';
import {
	collection,
	doc,
	DocumentData,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	QueryDocumentSnapshot,
	setDoc,
	startAfter,
	Timestamp,
	updateDoc,
	where,
} from 'firebase/firestore';
import generateTicketNumber from '../../functions/generateTicketNumber';
import { Group } from './groups';
import { AuthUser } from './auth';
import { adapterOldTicketToNewFormat } from '../../services/adapterOldTicketToNewFormat';

export interface TicketsState {
	apiData: TicketsApiData;
	tickets: NewTicketData[];
	ticket: NewTicketData | null;
	lastVisibleDate: number | null;
	hasMore: boolean;
	ticketsComments: Record<string, StorageItem<TicketCommentData[]>>;
	loading: boolean;
	error: string | null;
}

export interface TicketsPayload {
	apiData?: Partial<TicketsApiData>;
	tickets?: StorageItem<TicketData[]>;
	ticketsComments?: Record<string, StorageItem<TicketCommentData[]>>;
}

export interface TicketsApiData {
	groups: TicketsApiDataItem[];
	types: TicketsApiDataItem[];
	states: TicketsApiDataItem[];
	users: TicketsApiDataItem[];
}

export interface TicketsApiDataItem {
	id: string;
	name: string;
}

export interface TicketsApiCustomFieldData {
	name: string;
	description: string;
	required: boolean;
}

export interface TicketData {
	id: string;
	number: string;
	subject: string;
	description: string;
	group: TicketGroup;
	type: TicketType;
	state: TicketsApiDataItem;
	deadline: number;
	responsible: TicketsApiDataItem | null;
	creationDate: number;
	creationUser: TicketsApiDataItem;
	lastChangeDate: number;
	lastChangeUser: TicketsApiDataItem;
	endDate: number | null;
}

export interface NewTicketData {
	uid: string;
	ticketId?: string;
	title: string;
	description: string;
	shortDescription: string;
	group: string;
	type: TicketType;
	status: string;
	email: string;
	user: {
		name: string;
		uid: string;
	};
	number: string;
	creationDate: number;
	lastChangeDate: number;
	deadline?: number;
	responsible?: {
		name: string;
		uid?: string;
	};
	product?: TicketProduct;
	associatedUser?: {
		uid: string;
		email: string;
		cnpj: string;
		name: string;
		razao: string;
	};
	razao: string;
	ccEmails: string[];
	cnpj: string;
	phone: string;
	operationStatus: TicketOperationStatus;
	viewDetectorUid?: string;
}

export interface TicketCustomFieldData {
	question: string;
	answer: string;
	defaultForm: boolean;
}

export interface TicketCommentData {
	id: string;
	date: number;
	user: TicketsApiDataItem;
	content: string;
	public: boolean;
}

export interface TicketAttachmentData {
	id: string;
	name: string;
	url: string;
	mimeType: string;
	length: number;
	humanReadableLength: string;
	public: boolean;
}

export type TicketState = typeof FIXED_TICKET_STATES[number];

export type TicketProduct = typeof TICKET_PRODUCTS[number] | '';

export type TicketType = typeof TICKET_TYPES[number];

export type TicketGroup = typeof TICKET_GROUPS[number];

export type TicketCategory = typeof TICKET_CATEGORIES[number];

export type TicketEnvironment = typeof TICKET_ENVIRONMENTS[number];

export type TicketOperationStatus = typeof TICKET_OPERATION_STATUSES[number];

export type TicketImpactedUsers = typeof TICKET_IMPACTED_USERS[number];

export type TicketFinancialImpact = typeof TICKET_FINANCIAL_IMPACTS[number];

export type TicketPriority = typeof TICKET_PRIORITIES[number];

export type TicketStatus = {
	id: string;
	name: string;
	color: 'success' | 'secondary' | 'info' | 'primary' | 'error' | 'warning' | 'primary' | 'inherit';
};

export const FIXED_TICKET_STATES = [
	{ id: '1', name: 'Aberto', color: 'success' },
	{ id: '2', name: 'Aguardando resposta', color: 'inherit' },
	{ id: '3', name: 'Em andamento', color: 'info' },
	{ id: '4', name: 'Reaberto', color: 'primary' },
	{ id: '5', name: 'Finalizado', color: 'error' },
];

export const TICKET_TYPES = [
	'Dúvida',
	'Incidente',
	'Melhoria',
	'Não especificado',
	'Tarefa/Solicitação',
] as const;

export const TICKET_GROUPS = [
	'Administrativo/Financeiro',
	'Licitações - LicitaTaugor',
	'Marketing',
	'SAC - Serviço de Atendimento ao Cliente',
	'SAF - Serviço de Atendimento ao Franqueado',
	'Suporte Técnico',
] as const;

export const TICKET_PRODUCTS = [
	'AppCenter',
	'Dok+',
	'DokDrive',
	'EditFy',
	'Indexia',
	'YopenSign',
	'Workflux',
	'Taux-ERP',
	'CRM - Avatron',
] as const;

export const TICKET_CATEGORIES = [
	'Admin - Contrato e afins',
	'Admin - Multa Contratual',
	'Admin - Rescisão de Contrato',
	'API',
	'Com - Duvidas Comerciais',
	'Com - R.O - Registro de Oportunidade',
	'Finan - Novo Boleto',
	'Finan - Solicitação de Compra de Material',
	'Sup Téc - E-mail',
	'Sup Tec - Criação de Ambiente Novo',
	'Sup Tec - Duvidas',
	'Sup Tec - Especialista',
	'Mkt - Alteração de Site',
	'Mkt - Homologação Franquia - Videos e Fotos',
	'Mkt - Material Digital',
	'Mkt - Material Impresso',
	'Mkt - Site',
] as const;

export const TICKET_ENVIRONMENTS = [
	'Ambiente de produção - Cliente Ativo/Licença',
	'Dados/ambiente de teste - Somente Teste',
] as const;

export const TICKET_OPERATION_STATUSES = [
	'Operação Parada',
	'Operação consegue trabalhar',
] as const;

export const TICKET_IMPACTED_USERS = [
	'Apenas 1',
	'1 a 10 usuários',
	'11 a 30 usuários',
	'31 a 50 usuários',
	'mais de 50 usuários',
] as const;

export const TICKET_FINANCIAL_IMPACTS = [
	'Sem perda Financeira',
	'Com Perda financeira - Cliente Ativo ou Lead',
] as const;

export const TICKET_PRIORITIES = ['Baixa', 'Média', 'Alta'] as const;

const initialState: TicketsState = {
	apiData: {
		groups: [],
		types: [],
		states: [],
		users: [],
	},
	tickets: [],
	lastVisibleDate: null,
	hasMore: true,
	ticket: null,
	ticketsComments: {},
	loading: false,
	error: null,
};

// Create Ticket on Firebase
export const createTicket = createAsyncThunk(
	'tickets/createTicket',
	async (ticketData: NewTicketData, thunkApi) => {
		try {
			const ticketRef = doc(collection(inboxFirestore, 'tickets'));
			const ticketNumber = generateTicketNumber();

			await setDoc(ticketRef, {
				...ticketData,
				ticketId: ticketRef.id,
				creationDate: Timestamp.now().toMillis(),
				number: ticketNumber,
			});

			return { ...ticketData, ticketId: ticketRef.id, number: ticketNumber };
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

// Get tickets user
export const getTicketUser = createAsyncThunk(
	'tickets/getTicketUser',
	async (user: AuthUser, thunkApi) => {
		try {
			const q1 = query(collection(inboxFirestore, 'tickets'), where('uid', '==', user.uid));

			// Busca tickets onde o usuário foi destinatário (createdForEmail)
			const q2 = query(
				collection(inboxFirestore, 'tickets'),
				where('associatedUser.email', '==', user.email)
			);

			const [snapshot1, snapshot2] = await Promise.all([getDocs(q1), getDocs(q2)]);

			const tickets = [
				...snapshot1.docs.map((doc) => ({ ...doc.data() })),
				...snapshot2.docs.map((doc) => ({ ...doc.data() })),
			];

			// // Remove duplicados se houver
			// const uniqueTickets = tickets.filter(
			// 	(ticket, index, self) =>
			// 		index === self.findIndex((t) => t.id === ticket.id)
			// );

			return tickets;
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

// Get ticket by id
export const getTicketById = createAsyncThunk(
	'tickets/getTicketById',
	async (ticketId: string, thunkApi) => {
		try {
			const docRef = doc(inboxFirestore, 'tickets', ticketId);
			const docSnap = await getDoc(docRef);

			if (docSnap.exists()) {
				const data = docSnap.data();
				if ('formAnswers' in data) {
					return adapterOldTicketToNewFormat(data);
				}
				return data as NewTicketData;
			}

			return null;
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

// Update ticket
export const updateTicket = createAsyncThunk(
	'tickets/updateTicket',
	async (
		{ ticketId, updateFields }: { ticketId: string; updateFields: Partial<NewTicketData> },
		thunkApi
	) => {
		try {
			const docRef = doc(inboxFirestore, 'tickets', ticketId);

			const docSnap = await getDoc(docRef);
			if (!docSnap.exists()) {
				return thunkApi.rejectWithValue('Ticket not found');
			}

			const existingData = docSnap.data() as NewTicketData;
			const updatedData = {
				...existingData,
				...updateFields,
				lastChangeDate: Timestamp.now().toMillis(),
			};

			await updateDoc(docRef, updatedData);

			return { ticketId, ...updatedData };
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

// Get all tickets
export const getAllTickets = createAsyncThunk(
	'tickets/getAllTickets',
	async (
		{ pageSize, lastVisibleDate }: { pageSize: number; lastVisibleDate?: number },
		thunkApi
	) => {
		try {
			let q = query(
				collection(inboxFirestore, 'tickets'),
				orderBy('lastChangeDate', 'desc'),
				limit(pageSize)
			);

			if (lastVisibleDate) {
				q = query(
					collection(inboxFirestore, 'tickets'),
					orderBy('lastChangeDate', 'desc'),
					startAfter(lastVisibleDate),
					limit(pageSize)
				);
			}

			const querySnapshot = await getDocs(q);
			const tickets: NewTicketData[] = [];
			querySnapshot.forEach((doc) => {
				tickets.push(doc.data() as NewTicketData);
			});

			const newLastVisibleDate = tickets[tickets.length - 1]?.lastChangeDate;

			// console.log('old tickets', tickets);
			const adaptedTickets = tickets.map((ticket) =>
				'formAnswers' in ticket ? adapterOldTicketToNewFormat(ticket) : ticket
			);

			// console.log('adaptedTickets', adaptedTickets);
			return {
				tickets: adaptedTickets,
				lastVisibleDate: newLastVisibleDate,
				hasMore: querySnapshot.docs.length === pageSize,
			};
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const ticketsSlice = createSlice({
	name: 'tickets',
	initialState,
	reducers: {
		resetTickets: (state) => {
			state.tickets = [];
			state.lastVisibleDate = null;
			state.hasMore = true;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(createTicket.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createTicket.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.ticket = action.payload;
			})
			.addCase(createTicket.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(getTicketUser.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getTicketUser.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.tickets = action.payload as NewTicketData[];
			})
			.addCase(getTicketById.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getTicketById.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.ticket = action.payload;
			})
			.addCase(updateTicket.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateTicket.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				if (state.ticket?.ticketId === action.payload.ticketId) {
					state.ticket = {
						...state.ticket,
						...action.payload,
					};
				}
			})
			.addCase(getAllTickets.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getAllTickets.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;

				const { tickets, lastVisibleDate, hasMore } = action.payload;

				state.tickets = [...state.tickets, ...tickets];
				state.lastVisibleDate = lastVisibleDate;
				state.hasMore = hasMore;
			});
	},
});

export const useTickets = () => useSelector<RootState, TicketsState>((state) => state.tickets);
export default ticketsSlice.reducer;
